/**
 * Actual connection-speed assessment for the slow-search UI.
 *
 * The old behaviour showed "check your internet connection" purely on a 5s
 * timer — which blamed the user's wifi even when the pipeline itself was the
 * slow part (rerank + LLM legitimately take 6-10s). This measures instead:
 *
 *   1. `navigator.onLine` — cheap offline detection.
 *   2. Network Information API (Chrome/Android) — effectiveType/downlink/rtt
 *      as reported by the browser.
 *   3. An active probe: two timed GETs to the API's /health endpoint. The
 *      MINIMUM of the two is used as the RTT estimate — min, not mean,
 *      because the first request may pay DNS/TLS setup that says nothing
 *      about the link itself.
 *
 * Verdict is only "slow" when the evidence says so; otherwise the loading UI
 * shows "it's us thinking, not your connection" — which is the honest story.
 */

export type ConnectionVerdict = 'good' | 'slow' | 'offline' | 'unknown';

export interface ConnectionAssessment {
  verdict: ConnectionVerdict;
  /** Best RTT observed against the API, ms. null if the probe failed. */
  rttMs: number | null;
  /** From the Network Information API, when the browser exposes it. */
  effectiveType?: string;
  downlinkMbps?: number;
}

// Above this probe RTT the link is called slow. The API answers /health in
// ~1ms; production adds ~30-60ms of Mumbai RTT — so anything past 600ms is
// genuinely the network, with margin for a congested-but-fine connection.
const SLOW_RTT_MS = 600;
// Browser-reported downlink under this is treated as slow (Mbps).
const SLOW_DOWNLINK_MBPS = 1.0;
const PROBE_TIMEOUT_MS = 4000;

async function timedHealthProbe(baseUrl: string): Promise<number | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  const started = performance.now();
  try {
    // cache: 'no-store' so we measure the wire, not the HTTP cache.
    await fetch(`${baseUrl}/health`, {
      method: 'GET',
      cache: 'no-store',
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    });
    return performance.now() - started;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function assessConnection(baseUrl: string): Promise<ConnectionAssessment> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { verdict: 'offline', rttMs: null };
  }

  // Network Information API — not on Safari/Firefox, so it's advisory only.
  const conn: any = (navigator as any)?.connection;
  const effectiveType: string | undefined = conn?.effectiveType;
  const downlinkMbps: number | undefined =
    typeof conn?.downlink === 'number' ? conn.downlink : undefined;

  // Two sequential probes; keep the best. Sequential on purpose — parallel
  // probes on a thin link contend with each other and both read slow.
  const first = await timedHealthProbe(baseUrl);
  const second = await timedHealthProbe(baseUrl);
  const samples = [first, second].filter((v): v is number => v !== null);
  const rttMs = samples.length ? Math.min(...samples) : null;

  if (rttMs === null) {
    // Both probes failed while onLine claims true: either the API is down or
    // the connection is unusable. Don't blame the user's wifi with certainty.
    return { verdict: 'unknown', rttMs: null, effectiveType, downlinkMbps };
  }

  const browserSaysSlow =
    effectiveType === 'slow-2g' ||
    effectiveType === '2g' ||
    (downlinkMbps !== undefined && downlinkMbps < SLOW_DOWNLINK_MBPS);

  if (rttMs > SLOW_RTT_MS || browserSaysSlow) {
    return { verdict: 'slow', rttMs, effectiveType, downlinkMbps };
  }
  return { verdict: 'good', rttMs, effectiveType, downlinkMbps };
}
